import { useCallback, useEffect, useState } from "react";

// Stable localStorage key — schema-versioned so future migrations don't break.
const KEY = "tv.bookmarks.v1";

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function write(ids) {
  try {
    localStorage.setItem(KEY, JSON.stringify(ids));
  } catch {
    /* localStorage full or disabled — ignore. */
  }
}

/**
 * Persisted, cross-tab tool-bookmarks hook backed by localStorage.
 * Returns: { ids, has, toggle, clear, replace }
 *
 *   const { ids, has, toggle } = useBookmarks();
 *   toggle(toolId);
 *   if (has(toolId)) { ... }
 */
export default function useBookmarks() {
  const [ids, setIds] = useState(() => read());

  // Sync across tabs.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === KEY) setIds(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const persist = useCallback((next) => {
    setIds(next);
    write(next);
  }, []);

  const toggle = useCallback(
    (id) => {
      if (!id) return;
      persist(
        ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
      );
    },
    [ids, persist],
  );

  const has = useCallback((id) => ids.includes(id), [ids]);

  const clear = useCallback(() => persist([]), [persist]);

  const replace = useCallback((next) => persist(next || []), [persist]);

  return { ids, has, toggle, clear, replace };
}
